'use client';

import React, {
  forwardRef,
  useCallback,
  useRef,
  useImperativeHandle,
  useMemo,
  useState,
  type VideoHTMLAttributes,
} from 'react';
import { useMediaDelivery } from '@/hooks/useMediaDelivery';
import type { MediaDelivery } from '@/lib/media-delivery';
import { buildVideoPlaybackUrls } from '@/lib/task-media';
import { useVideoElement } from '@/lib/hooks/useVideoElement';

export interface PlaybackVideoProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src'> {
  src?: string | null;
  assetId?: string | null;
  delivery?: MediaDelivery;
  variant?: 'preview' | 'playback';
}

/** Shared browser video element with direct-URL -> asset-stream failover. */
export const PlaybackVideo = forwardRef<HTMLVideoElement, PlaybackVideoProps>(function PlaybackVideo(
  { src, assetId, delivery: suppliedDelivery, variant = 'playback', onError, ...videoProps },
  ref,
) {
  const { video, videoRef, autoPlayAllowed, playbackRevision, isPlaybackAllowed } = useVideoElement();
  useImperativeHandle(ref, () => video.current!, [video]);
  const resolved = useMediaDelivery(assetId, !suppliedDelivery);
  // An account/source change can leave a previous snapshot available for one
  // render. A derivative is valid only for this exact asset identity.
  const candidate = suppliedDelivery || resolved.data;
  const delivery = !assetId || candidate?.asset_id === assetId ? candidate : undefined;
  const preferredUrl = delivery?.video?.[variant]?.url || delivery?.video?.playback?.url;
  const playbackUrls = useMemo(
    () => Array.from(new Set([
      ...(preferredUrl ? [preferredUrl] : []),
      ...buildVideoPlaybackUrls({ videoUrl: delivery?.original?.url || src, assetId }),
    ])),
    [assetId, src, preferredUrl, delivery?.original?.url],
  );
  const identity = `${assetId || ''}:${src || ''}:${variant}`;
  const sourcesKey = `${identity}:${playbackUrls.join('\u0000')}`;
  const [failedSource, setFailedSource] = useState<{ key: string; index: number } | null>(null);
  const sourceIndex = failedSource?.key === sourcesKey ? failedSource.index : 0;
  const [pinnedSource, setPinnedSource] = useState<{ identity: string; url: string } | null>(null);
  const pinnedUrl = pinnedSource?.identity === identity ? pinnedSource.url : undefined;
  const activeUrl = pinnedUrl || playbackUrls[sourceIndex];
  const resume = useRef<{ identity: string; time: number; paused: boolean; revision: number } | null>(null);

  const handleError = useCallback<NonNullable<VideoHTMLAttributes<HTMLVideoElement>['onError']>>((event) => {
    // A refreshed signature/prepared variant must not restart an already playing video.
    // If its pinned URL later fails, switch using the latest delivery and retain position.
    const nextIndex = pinnedUrl && pinnedUrl !== playbackUrls[sourceIndex] ? 0 : sourceIndex + 1;
    if (nextIndex < playbackUrls.length) {
      resume.current = { identity, time: event.currentTarget.currentTime, paused: event.currentTarget.paused, revision: playbackRevision.current };
      setPinnedSource(null);
      setFailedSource({ key: sourcesKey, index: nextIndex });
      return;
    }
    onError?.(event);
  }, [identity, onError, pinnedUrl, playbackUrls, sourceIndex, sourcesKey, playbackRevision]);

  return (
    <video
      {...videoProps}
      autoPlay={videoProps.autoPlay && autoPlayAllowed}
      ref={videoRef}
      poster={delivery?.video?.poster?.url || videoProps.poster}
      src={pinnedUrl || (resolved.loading && !delivery ? undefined : activeUrl || undefined)}
      onPlay={(event) => {
        const url = event.currentTarget.getAttribute('src');
        if (url) setPinnedSource({ identity, url });
        videoProps.onPlay?.(event);
      }}
      onLoadedMetadata={(event) => {
        const pending = resume.current;
        resume.current = null;
        if (pending?.identity === identity) {
          const video = event.currentTarget;
          video.currentTime = Math.min(pending.time, Number.isFinite(video.duration) ? video.duration : pending.time);
          if (pending.paused) video.pause();
          else if (pending.revision === playbackRevision.current && isPlaybackAllowed()) void video.play().catch(() => { /* Browser playback permission may require another user gesture. */ });
        }
        videoProps.onLoadedMetadata?.(event);
      }}
      onError={handleError}
    />
  );
});
